const LOCAL_DEV_API_BASE_URL = process.env.NODE_ENV === 'production'
  ? ''
  : 'http://localhost:8000/api/v1';

function readEnv(env, key) {
  if (!env || typeof env !== 'object') {
    return undefined;
  }
  const value = env[key];
  if (value == null) {
    return undefined;
  }
  const trimmed = String(value).trim();
  return trimmed === '' ? undefined : trimmed;
}

function isProductionEnv(env) {
  return readEnv(env, 'NODE_ENV') === 'production';
}

function normalizeApiBaseUrl(value) {
  return String(value).trim().replace(/\/+$/, '');
}

function isLoopbackHostname(hostname) {
  const host = String(hostname || '').toLowerCase().replace(/^\[|\]$/g, '');
  return host === 'localhost' || host === '127.0.0.1' || host === '::1' || host.endsWith('.localhost');
}

function isUsableApiBaseUrl(value, { production = false } = {}) {
  if (!value) {
    return false;
  }
  try {
    const parsed = new URL(value);
    if (production) {
      if (parsed.protocol !== 'https:') {
        return false;
      }
      if (isLoopbackHostname(parsed.hostname)) {
        return false;
      }
    } else if (!['http:', 'https:'].includes(parsed.protocol)) {
      return false;
    }
    return parsed.pathname.includes('/api/v1') || value.includes('/api/v1');
  } catch {
    return false;
  }
}

export function resolveApiBaseUrl(env = process.env) {
  const configured = readEnv(env, 'REACT_APP_API_BASE_URL');
  const production = isProductionEnv(env);

  if (!configured) {
    if (production || process.env.NODE_ENV === 'production') {
      throw new Error('REACT_APP_API_BASE_URL is required in production and must be an absolute HTTPS URL containing /api/v1.');
    }
    return LOCAL_DEV_API_BASE_URL;
  }

  const normalized = normalizeApiBaseUrl(configured);
  if (!isUsableApiBaseUrl(normalized, { production })) {
    if (production) {
      throw new Error('REACT_APP_API_BASE_URL must be an absolute HTTPS URL containing /api/v1 and must not use a localhost API fallback.');
    }
    throw new Error('REACT_APP_API_BASE_URL is malformed. It must be an absolute URL containing /api/v1.');
  }

  return normalized;
}

export const API_BASE_URL = resolveApiBaseUrl();

export const endpoints = {
  login: '/admin/login',
  refresh: '/admin/refresh',
  me: '/admin/me',
  logout: '/admin/logout',
  overview: '/admin/professional-assessment/overview',
  patterns: '/admin/professional-assessment/patterns',
  pattern: (id) => `/admin/professional-assessment/patterns/${id}`,
  patternActivation: (id) => `/admin/professional-assessment/patterns/${id}/activation`,
  patternWeightMatrix: (id) => `/admin/professional-assessment/patterns/${id}/weight-matrix`,
  sections: '/admin/professional-assessment/sections',
  sectionsReorder: '/admin/professional-assessment/sections/reorder',
  section: (id) => `/admin/professional-assessment/sections/${id}`,
  questions: '/admin/professional-assessment/questions',
  questionsReorder: '/admin/professional-assessment/questions/reorder',
  question: (id) => `/admin/professional-assessment/questions/${id}`,
  questionActivation: (id) => `/admin/professional-assessment/questions/${id}/activation`,
  questionConfiguration: (id) => `/admin/professional-assessment/questions/${id}/configuration`,
  questionAnswers: (id) => `/admin/professional-assessment/questions/${id}/answers`,
  answer: (id) => `/admin/professional-assessment/answers/${id}`,
  answerWeights: (id) => `/admin/professional-assessment/answers/${id}/weights`,
  versions: '/admin/professional-assessment/versions',
  versionsPreflight: '/admin/professional-assessment/versions/preflight',
  versionsBuild: '/admin/professional-assessment/versions/build',
  version: (id) => `/admin/professional-assessment/versions/${id}`,
  versionPublish: (id) => `/admin/professional-assessment/versions/${id}/publish`,
  versionAcknowledgeZeroWeights: (id) => `/admin/professional-assessment/versions/${id}/acknowledge-zero-weights`,
  versionTopThreeProfiles: (id) => `/admin/professional-assessment/versions/${id}/top-three-profiles`,
  versionTopThreeProfile: (versionId, profileId) => `/admin/professional-assessment/versions/${versionId}/top-three-profiles/${profileId}`,
  versionTopThreeProfileCoverage: (id) => `/admin/professional-assessment/versions/${id}/top-three-profiles/coverage`,
  versionTopThreeProfilesRepair: (id) => `/admin/professional-assessment/versions/${id}/top-three-profiles/repair`,
  tags: '/admin/professional-assessment/tags',
  tag: (id) => `/admin/professional-assessment/tags/${id}`,
  tagActivation: (id) => `/admin/professional-assessment/tags/${id}/activation`,
  personalSummaries: '/admin/professional-assessment/personal-summaries',
  personalSummary: (id) => `/admin/professional-assessment/personal-summaries/${id}`,
  personalSummaryActivation: (id) => `/admin/professional-assessment/personal-summaries/${id}/activation`,
  resultItems: '/admin/professional-assessment/result-items',
  resultItem: (id) => `/admin/professional-assessment/result-items/${id}`,
  resultItemActivation: (id) => `/admin/professional-assessment/result-items/${id}/activation`,
  attempts: '/admin/professional-assessment/attempts',
  attempt: (id) => `/admin/professional-assessment/attempts/${id}`,
  questionnaireTemplate: '/admin/professional-assessment/workbooks/questionnaire/template',
  questionnaireExport: '/admin/professional-assessment/workbooks/questionnaire/export',
  questionnaireImportPreview: '/admin/professional-assessment/workbooks/questionnaire/import/preview',
  questionnaireImportCommit: '/admin/professional-assessment/workbooks/questionnaire/import/commit',
  newQuestionsTemplate: '/admin/professional-assessment/workbooks/questionnaire/new-questions-template',
  newQuestionsImportPreview: '/admin/professional-assessment/workbooks/questionnaire/new-questions/preview',
  newQuestionsImportCommit: '/admin/professional-assessment/workbooks/questionnaire/new-questions/commit',
  versionTopThreeProfilesTemplate: (id) => `/admin/professional-assessment/versions/${id}/workbooks/top-three-profiles/template`,
  versionTopThreeProfilesExport: (id) => `/admin/professional-assessment/versions/${id}/workbooks/top-three-profiles/export`,
  versionTopThreeProfilesImportPreview: (id) => `/admin/professional-assessment/versions/${id}/workbooks/top-three-profiles/preview`,
  versionTopThreeProfilesImportCommit: (id) => `/admin/professional-assessment/versions/${id}/workbooks/top-three-profiles/commit`,
  specialists: '/admin/professional-assessment/specialists',
  specialist: (id) => `/admin/professional-assessment/specialists/${id}`,
  specialistActivation: (id) => `/admin/professional-assessment/specialists/${id}/activation`,
  consultations: '/admin/professional-assessment/consultations',
  consultation: (id) => `/admin/professional-assessment/consultations/${id}`,
  consultationHistory: (id) => `/admin/professional-assessment/consultations/${id}/history`,
  consultationAssign: (id) => `/admin/professional-assessment/consultations/${id}/assign`,
  consultationSchedule: (id) => `/admin/professional-assessment/consultations/${id}/schedule`,
  consultationComplete: (id) => `/admin/professional-assessment/consultations/${id}/complete`,
  consultationReject: (id) => `/admin/professional-assessment/consultations/${id}/reject`,
  consultationCancel: (id) => `/admin/professional-assessment/consultations/${id}/cancel`,
  supportCategories: '/admin/support/categories',
  supportCategory: (id) => `/admin/support/categories/${id}`,
  supportTickets: '/admin/support/tickets',
  supportTicket: (id) => `/admin/support/tickets/${id}`,
  supportTicketReview: (id) => `/admin/support/tickets/${id}/review`,
  supportTicketResolve: (id) => `/admin/support/tickets/${id}/resolve`,
  supportTicketClose: (id) => `/admin/support/tickets/${id}/close`,
  supportTicketReopen: (id) => `/admin/support/tickets/${id}/reopen`,
  legalDocuments: '/admin/legal-documents',
  legalDocument: (id) => `/admin/legal-documents/${id}`,
  legalDocumentPublish: (id) => `/admin/legal-documents/${id}/publish`,
  companyPlans: '/admin/professional-assessment/plans',
  companyPlan: (id) => `/admin/professional-assessment/plans/${id}`,
  companyPlanActivation: (id) => `/admin/professional-assessment/plans/${id}/activation`,
  companies: '/admin/professional-assessment/companies',
  company: (id) => `/admin/professional-assessment/companies/${id}`,
  companyStatus: (id) => `/admin/professional-assessment/companies/${id}/status`,
  planPurchases: '/admin/professional-assessment/purchases',
  planPurchase: (id) => `/admin/professional-assessment/purchases/${id}`,
  planPurchaseConfirmPayment: (id) => `/admin/professional-assessment/purchases/${id}/confirm-payment`,
  companyEntitlements: '/admin/professional-assessment/entitlements',
  companyEntitlement: (id) => `/admin/professional-assessment/entitlements/${id}`,
  companyEntitlementSuspend: (id) => `/admin/professional-assessment/entitlements/${id}/suspend`,
  companyEntitlementRestore: (id) => `/admin/professional-assessment/entitlements/${id}/restore`,
  companyEntitlementRevoke: (id) => `/admin/professional-assessment/entitlements/${id}/revoke`,
  accessCodeBatches: '/admin/professional-assessment/code-batches',
  accessCodeBatch: (id) => `/admin/professional-assessment/code-batches/${id}`,
  accessCodes: '/admin/professional-assessment/access-codes',
  accessCode: (id) => `/admin/professional-assessment/access-codes/${id}`,
  accessCodeRevoke: (id) => `/admin/professional-assessment/access-codes/${id}/revoke`,
  sponsoredAssessments: '/admin/professional-assessment/sponsored-assessments',
  sponsoredAssessment: (id) => `/admin/professional-assessment/sponsored-assessments/${id}`,
};
